import React, { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";

import { formatDate } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { Button, Form, Icon, Loader, Message, Table } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";
import { Modal } from "simplydo/react/ui";

import api from "api";
import { countries } from "utils/countries";
import util from "utils/utils";

type VerificationReport =
  OpenAPI.GET<"/verifications/companies/reports">["response"]["reports"][number];
type VerificationCompany =
  OpenAPI.GET<"/verifications/companies/search">["response"]["companies"][number];

type CompanyVerificationReportGeneratorProps = {
  user: UserMe;
  trigger?: React.ReactElement<{ onClick?: () => void }>;
  forId: string;
  forType: "idea" | "org";
  content?: string;
  onComplete: (report: VerificationReport) => void;
  onError?: (error: { creditsafeEnabled: boolean; message: string }) => void;
};

function CompanyVerificationReportGenerator({
  user,
  trigger,
  forId,
  forType,
  content,
  onComplete,
  onError,
}: CompanyVerificationReportGeneratorProps) {
  const [companies, setCompanies] = useState<VerificationCompany[] | undefined>(
    undefined,
  );
  const [country, setCountry] = useState("GB");
  const [companyNumber, setCompanyNumber] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [reports, setReports] = useState<VerificationReport[]>([]);
  const [loadingReports, setLoadingReports] = useState<string | boolean>(false);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const userId = user?._id;

  useEffect(() => {
    if (open && userId) {
      setLoadingReports(true);
      api.verifications.getReports(
        (data) => {
          setLoadingReports(false);
          setReports(data.reports);
        },
        () => setLoadingReports(false),
      );
    }
  }, [userId, open]);

  const openModal = () => {
    setOpen(!open);
  };

  const searchForCompanies = useCallback(() => {
    setSearching(true);
    api.verifications.searchForCompanies(
      country,
      companyNumber,
      companyName,
      forType,
      forId,
      (data) => {
        setSearching(false);
        setCompanies(data.companies);
      },
      (err) => {
        setSearching(false);
        if (err.status === 503) {
          setOpen(false);
          onError?.({
            creditsafeEnabled: false,
            message:
              "We have detected an issue with the company lookup service. Please enter your information manually or contact support for assistance",
          });
        }
      },
    );
  }, [forType, forId, country, companyNumber, companyName, onError]);

  const chooseReport = useCallback(
    (report: VerificationReport) => {
      onComplete(report);
      setOpen(false);
    },
    [onComplete],
  );

  const generateReportForCompany = useCallback(
    (company: VerificationCompany) => {
      if (!company.regNo) return;
      setLoadingReports(company.regNo);
      api.verifications.createReport(
        { id: company._id, forType, forId },
        (data) => {
          onComplete(data.report);
          setLoadingReports(false);
          setOpen(false);
        },
        (err) => {
          toast.error(err.message);
          setLoadingReports(false);
        },
      );
    },
    [forType, forId, onComplete],
  );

  return (
    <>
      {trigger ? (
        React.cloneElement(trigger, { onClick: openModal })
      ) : (
        <Button
          size="small"
          secondary
          icon="file outline"
          fluid
          content={content || "Generate a new report"}
          onClick={openModal}
        />
      )}
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={open}
        dimmer="inverted"
        size="tiny"
        style={{ zIndex: 100 }}
        closeOnDocumentClick={false}
      >
        <Modal.Header>
          <Icon name="check circle" /> Company lookup
        </Modal.Header>
        <Modal.Content scrolling>
          <h3>Your companies</h3>
          {loadingReports && <Loader active inline="centered" />}
          {reports?.length > 0 ? (
            <Table basic="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Company name</Table.HeaderCell>
                  <Table.HeaderCell>Looked-up at</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {reports.map((report) => (
                  <Table.Row key={report._id}>
                    <Table.Cell>
                      {report.companyName}
                      <br />
                      <small>{report.companyNumber}</small>
                    </Table.Cell>
                    <Table.Cell>{formatDate(report.createdAt)}</Table.Cell>
                    <Table.Cell collapsing>
                      <Button
                        size="tiny"
                        onClick={() => chooseReport(report)}
                        primary
                        content="Use this company"
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            !loadingReports && (
              <Message info>
                You haven&apos;t looked-up any companies yet.
              </Message>
            )
          )}

          <h3>New company lookup</h3>
          <p>
            Search for your company by providing its country of registration{" "}
            <strong>and</strong> either its name or registration number.
          </p>

          <Form onSubmit={searchForCompanies}>
            <Form.Field>
              <label htmlFor="registrationCountry">
                Registration country (required)
              </label>
              <Form.Select
                search
                id="registrationCountry"
                value={country}
                onChange={(_e, s) => setCountry(`${s.value ?? "GB"}`)}
                placeholder="Select one"
                options={countries.map((c) => ({
                  key: c.iso2,
                  value: c.iso2,
                  text: c.name,
                }))}
              />
            </Form.Field>

            <Form.Group stackable>
              <Form.Field width={8}>
                <label htmlFor="registrationNumber">Registration number</label>
                <Form.Field
                  control={Input}
                  id="registrationNumber"
                  value={companyNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCompanyNumber(e.target.value)
                  }
                  placeholder="12345678"
                />
              </Form.Field>
              <Form.Field width={8}>
                <label htmlFor="companyName">Company name</label>
                <Form.Field
                  control={Input}
                  id="companyName"
                  value={companyName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCompanyName(e.target.value)
                  }
                  placeholder="My Company Ltd"
                />
              </Form.Field>
            </Form.Group>
            <Form.Button
              loading={searching}
              type="submit"
              secondary
              content="Search"
              icon="search"
            />
          </Form>
          {companies?.length === 0 && (
            <Message size="small">
              We were unable to find any companies matching your search
              criteria.
            </Message>
          )}
          {companies && companies.length > 0 && (
            <Table basic="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Company</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {companies.map((company) => (
                  <Table.Row key={company.id}>
                    <Table.Cell>
                      {company.name}
                      <br />
                      <small>
                        <strong>{company.regNo}</strong>
                        <br />
                        {company.address?.simpleValue}
                      </small>
                    </Table.Cell>
                    <Table.Cell collapsing>
                      <Button
                        size="tiny"
                        loading={loadingReports === company.regNo}
                        disabled={
                          !!loadingReports && loadingReports !== company.regNo
                        }
                        primary
                        icon="check circle"
                        content="Use this company"
                        onClick={() => generateReportForCompany(company)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={openModal} content="Close" />
        </Modal.Actions>
      </Modal>
    </>
  );
}

export default CompanyVerificationReportGenerator;
